import React, { useEffect, useState, useRef } from "react";
import { api, API as API_BASE } from "../../lib/api";
import { toast } from "sonner";
import { Upload, FileAudio, Loader2, Download, Trash2, Save, X, Plus } from "lucide-react";

/**
 * AI-generated trustee meeting minutes.
 *  1. Upload audio (mp3/m4a/wav/webm ≤ 25 MB).
 *  2. Backend transcribes via Whisper + structures via Claude.
 *  3. Trustee can edit fields inline and export to PDF.
 */
export default function MeetingMinutesManager() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [meetingDate, setMeetingDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/minutes");
      setItems(r.data.items || []);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose an audio file (.mp3/.m4a/.wav/.webm).");
    if (f.size > 25 * 1024 * 1024) return toast.error("Audio exceeds 25 MB limit.");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    const params = new URLSearchParams();
    if (title) params.append("title", title);
    if (meetingDate) params.append("meeting_date", meetingDate);
    try {
      const r = await fetch(`${API_BASE}/admin/minutes/transcribe?${params.toString()}`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!r.ok) throw new Error(`Transcription failed (${r.status})`);
      const doc = await r.json();
      toast.success("Minutes generated.");
      setTitle(""); setMeetingDate("");
      if (fileRef.current) fileRef.current.value = "";
      setSelected(doc);
      load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    setUploading(false);
  };

  const openOne = async (id) => {
    try {
      const r = await api.get(`/admin/minutes/${id}`);
      setSelected(r.data);
    } catch (e) { toast.error(e.message); }
  };

  const removeOne = async (id) => {
    if (!confirm("Delete these minutes permanently?")) return;
    try {
      await api.delete(`/admin/minutes/${id}`);
      toast.success("Deleted");
      if (selected?.minutes_id === id) setSelected(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (selected) return <MinutesEditor doc={selected} onClose={() => { setSelected(null); load(); }} />;

  return (
    <div className="space-y-6" data-testid="minutes-manager">
      <form onSubmit={upload} className="bg-white border border-navy/10 rounded-sm p-5 space-y-3">
        <div className="flex items-center gap-2">
          <FileAudio size={18} className="text-saffron" />
          <h3 className="font-display text-lg text-navy">New minutes from audio</h3>
        </div>
        <p className="text-xs text-slate-500">
          Upload a recording of a trustee meeting. Whisper transcribes it, Claude extracts the
          minutes (attendees, decisions, actions, risks). Editable afterwards.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text" placeholder="Meeting title (e.g., March Trustee Meeting)"
            value={title} onChange={(e) => setTitle(e.target.value)}
            data-testid="minutes-title-input"
            className="border border-navy/15 rounded-sm px-3 py-2 text-sm"
          />
          <input
            type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)}
            data-testid="minutes-date-input"
            className="border border-navy/15 rounded-sm px-3 py-2 text-sm"
          />
          <input
            ref={fileRef} type="file" accept=".mp3,.m4a,.wav,.webm,.mp4,.mpeg,.mpga"
            data-testid="minutes-file-input"
            className="border border-navy/15 rounded-sm px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={uploading}
          data-testid="minutes-upload-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-saffron text-navy text-sm rounded-sm font-medium hover:bg-saffron-light disabled:opacity-60"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Transcribing… (~30-90s)" : "Transcribe & structure"}
        </button>
      </form>

      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="font-display text-xl text-navy">Past minutes</h3>
          <span className="text-xs text-slate-500">{items.length} saved</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="minutes-list">
            <thead>
              <tr className="text-left border-b border-navy/10 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="py-2 pr-3">Title</th>
                <th className="py-2 pr-3">Meeting date</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">By</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="py-4 italic text-slate-500">Loading…</td></tr>}
              {!loading && items.map((m) => (
                <tr key={m.minutes_id} className="border-b border-navy/5">
                  <td className="py-2 pr-3 text-navy font-medium">{m.title}</td>
                  <td className="py-2 pr-3 text-xs text-slate-600">{m.meeting_date}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{new Date(m.created_at).toLocaleString()}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{m.created_by}</td>
                  <td className="py-2 pr-3 text-right space-x-2">
                    <button onClick={() => openOne(m.minutes_id)} data-testid={`minutes-open-${m.minutes_id}`} className="text-xs text-navy underline">Open</button>
                    <a href={`${API_BASE}/admin/minutes/${m.minutes_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-saffron underline inline-flex items-center gap-1"><Download size={11} /> PDF</a>
                    <button onClick={() => removeOne(m.minutes_id)} className="text-xs text-red-600 underline inline-flex items-center gap-1"><Trash2 size={11} /> Delete</button>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={5} className="py-4 italic text-slate-500">No minutes yet. Upload an audio recording above.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MinutesEditor({ doc, onClose }) {
  const [m, setM] = useState(doc);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setM((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: m.title, meeting_date: m.meeting_date, summary: m.summary,
        attendees: m.attendees, apologies: m.apologies, agenda: m.agenda,
        decisions: m.decisions, action_items: m.action_items, risks: m.risks,
        next_meeting: m.next_meeting,
      };
      const r = await api.put(`/admin/minutes/${m.minutes_id}`, body);
      toast.success("Saved");
      setM(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const updateList = (key, items) => set(key, items);

  return (
    <div className="space-y-5" data-testid="minutes-editor">
      <div className="flex items-center gap-3">
        <button onClick={onClose} className="text-slate-500 hover:text-navy text-sm inline-flex items-center gap-1"><X size={14} /> Close</button>
        <h3 className="font-display text-xl text-navy flex-1">Edit minutes</h3>
        <a href={`${API_BASE}/admin/minutes/${m.minutes_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-saffron underline inline-flex items-center gap-1"><Download size={12} /> Download PDF</a>
        <button onClick={save} disabled={saving} data-testid="minutes-save-btn" className="inline-flex items-center gap-1 px-3 py-1.5 bg-navy text-pearl rounded-sm text-xs font-medium hover:bg-navy-light disabled:opacity-50">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save changes
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Title" value={m.title} onChange={(v) => set("title", v)} />
        <Field label="Meeting date" value={m.meeting_date} onChange={(v) => set("meeting_date", v)} />
      </div>

      <Block label="Summary">
        <textarea value={m.summary || ""} onChange={(e) => set("summary", e.target.value)} rows={4}
          className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm" />
      </Block>

      <ListEditor label="Attendees" items={m.attendees || []} onChange={(v) => updateList("attendees", v)} />
      <ListEditor label="Apologies" items={m.apologies || []} onChange={(v) => updateList("apologies", v)} />
      <ListEditor label="Agenda items" items={m.agenda || []} onChange={(v) => updateList("agenda", v)} />

      <ObjectListEditor
        label="Decisions"
        items={m.decisions || []}
        onChange={(v) => updateList("decisions", v)}
        fields={[{ key: "item", placeholder: "Item" }, { key: "decision", placeholder: "Decision" }]}
      />
      <ObjectListEditor
        label="Action items"
        items={m.action_items || []}
        onChange={(v) => updateList("action_items", v)}
        fields={[
          { key: "owner", placeholder: "Owner" },
          { key: "task", placeholder: "Task" },
          { key: "due", placeholder: "Due (YYYY-MM-DD or TBA)" },
        ]}
      />
      <ListEditor label="Risks" items={m.risks || []} onChange={(v) => updateList("risks", v)} />
      <Block label="Next meeting">
        <input value={m.next_meeting || ""} onChange={(e) => set("next_meeting", e.target.value)}
          className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm" />
      </Block>

      <details className="bg-pearl/40 rounded-sm p-3">
        <summary className="cursor-pointer text-xs uppercase tracking-widest text-slate-500">Raw transcript</summary>
        <pre className="mt-3 text-xs whitespace-pre-wrap text-slate-700 max-h-60 overflow-y-auto">{m.transcript || "—"}</pre>
      </details>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm" />
    </label>
  );
}
function Block({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
function ListEditor({ label, items, onChange }) {
  const upd = (idx, v) => onChange(items.map((it, i) => i === idx ? v : it));
  const add = () => onChange([...items, ""]);
  const rm = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <Block label={label}>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input value={it} onChange={(e) => upd(i, e.target.value)} className="flex-1 border border-navy/15 rounded-sm px-3 py-1.5 text-sm" />
            <button onClick={() => rm(i)} className="text-red-600 text-xs"><Trash2 size={13} /></button>
          </div>
        ))}
        <button onClick={add} type="button" className="text-xs text-saffron inline-flex items-center gap-1"><Plus size={12} /> Add</button>
      </div>
    </Block>
  );
}
function ObjectListEditor({ label, items, onChange, fields }) {
  const upd = (idx, key, val) => onChange(items.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const rm = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <Block label={label}>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-start">
            {fields.map((f) => (
              <input key={f.key} placeholder={f.placeholder} value={it[f.key] || ""} onChange={(e) => upd(i, f.key, e.target.value)}
                className="flex-1 border border-navy/15 rounded-sm px-2 py-1.5 text-sm" />
            ))}
            <button onClick={() => rm(i)} className="text-red-600 text-xs pt-2"><Trash2 size={13} /></button>
          </div>
        ))}
        <button onClick={add} type="button" className="text-xs text-saffron inline-flex items-center gap-1"><Plus size={12} /> Add</button>
      </div>
    </Block>
  );
}
